import React, { ChangeEvent } from 'react';
import { ActionProps } from '@/components/Dialog';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import Page from '@/model/Page';
import Email from '@/model/Email';
import { ReactComponent as Pen } from '@/assets/images/svg/pen.svg';
import { CustomTable, TableColumn } from '@/components/Table';
import { Button, Dialog, Loading } from '@/components';
import { Container, Label } from 'reactstrap';
import FilterVector from '@/assets/images/svg/FilterVector';
import Pagination from '@/components/Utils/Pagination';
import EmailType from '@/model/EmailType';
import { DataTable, ShouldShowModal, States } from '../../types';
import { FilterEmail } from './FilterEmail';
import { SaveEmail } from './SaveEmail';

interface StateProps {
  state: States;
  modalTitle: string | React.ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  renderActionDialogToClearFilter: ActionProps;
  renderModalActionProps: ActionProps;
  email?: Email;
  formDataFilter: FormData;
  formErrorsFilter: FormErrors;
  formDataEmail: FormData;
  formErrorsEmail: FormErrors;
  title: string;
  emails: Page<Email, Email>;
  togglePassword: boolean;
}

interface DispatchProps {
  onToggle(): void;
  onFilter(): Promise<void>;
  save(): Promise<void>;
  changeFormInputFilter: OnChangeFormInput;
  onChangeFormInputEmail: OnChangeFormInput;
  openModal(value: ShouldShowModal, modalTitle: string, email?: Email): void;
  onActivateAndInactivate(e: ChangeEvent<HTMLInputElement>): void;
  setTogglePassword: (b: boolean) => void;
  paginationChange(page: number): void;
}

type Props = StateProps & DispatchProps;

export const EmailList: React.FC<Props> = (props: Props): JSX.Element => {
  const emailTypeToString = (emailType: EmailType): string => {
    let s = '';
    switch (emailType) {
      case EmailType.PAYMENT:
        s = 'Pagamento';
        break;
      case EmailType.CONTACT:
        s = 'Contato';
        break;
      case EmailType.NOT_REPPLY:
        s = 'Não responda';
        break;
      default:
        s = 'Contato';
        break;
    }
    return s;
  };

  const dataTable = props.emails.list?.map(data => ({
    name: data.name,
    userName: data.userName,
    type: emailTypeToString(data.type),
    actived: data.actived ? 'Sim' : 'Não',
    amountSend: data.amountSend,
    actions: (
      <div className="d-flex">
        <Pen
          onClick={(): void => props.openModal(ShouldShowModal.email, 'Editar email', data)}
          className="mr-4 svg-icon action-icon"
        />
      </div>
    ),
  }));

  const columns: TableColumn<DataTable>[] = [
    {
      name: 'Nome',
      selector: row => row.name,
    },
    {
      name: 'Email',
      selector: row => row.userName,
    },
    {
      name: 'Tipo',
      selector: row => row.type,
    },
    {
      name: 'Enviados',
      selector: row => row.amountSend,
    },
    {
      name: 'Ativo',
      selector: row => row.actived,
    },
    {
      name: 'Ações',
      selector: row => row.actions,
      width: '115px',
    },
  ];
  return (
    <React.Fragment>
      <Loading isVisible={props.state === States.loading} />
      <Dialog
        title={props.modalTitle}
        visible={props.visible}
        onClose={props.onToggle}
        position="center"
        isContentWithCard={props.shouldShowModal !== ShouldShowModal.email}
        actions={[
          {
            [ShouldShowModal.filter]: props.renderActionDialogToClearFilter,
            [ShouldShowModal.email]: props.renderModalActionProps,
          }[props.shouldShowModal],
          {
            [ShouldShowModal.filter]: {
              title: 'Aplicar',
              onClick: (): Promise<void> => props.onFilter(),
            },
            [ShouldShowModal.email]: {
              title: props.email?.id ? 'Editar email' : 'Cadastrar email',
              onClick: (): Promise<void> => props.save(),
            },
          }[props.shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.filter]: (
              <FilterEmail
                formData={props.formDataFilter}
                formErrors={props.formErrorsFilter}
                change={props.changeFormInputFilter}
              />
            ),
            [ShouldShowModal.email]: (
              <SaveEmail
                formData={props.formDataEmail}
                formErrors={props.formErrorsEmail}
                togglePassword={props.togglePassword}
                onActivateAndInactivate={props.onActivateAndInactivate}
                change={props.onChangeFormInputEmail}
                setTogglePassword={props.setTogglePassword}
              />
            ),
          }[props.shouldShowModal]
        }
      </Dialog>
      <Container className="mainContainer" fluid={true}>
        <div className="d-flex justify-content-between" style={{ paddingBottom: '30px' }}>
          <div className="pageTitle" style={{ display: 'grid' }}>
            <Label>{props.title}</Label>
          </div>
          <div className="button-filter-container">
            <Button
              size="md"
              title="+ Cadastrar email"
              onClick={(): void => props.openModal(ShouldShowModal.email, 'Cadastrar email')}
            />
            <div className="filter-container">
              <div
                className="filter-content"
                onClick={(): void => props.openModal(ShouldShowModal.filter, '')}
              >
                <FilterVector />
              </div>
            </div>
          </div>
        </div>
        <CustomTable
          columns={columns}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data={dataTable as any[]}
          numberRowsPerPage={props.emails.pageSize as number}
          progressPending={props.state === States.loading}
        />
        <Pagination
          currentPage={props.emails.page}
          totalCount={props.emails.total}
          pageSize={props.emails.pageSize}
          onPageChange={page => props.paginationChange(page)}
          total={props.emails.total}
        />
      </Container>
    </React.Fragment>
  );
};
